import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PiArrowSquareOut } from 'react-icons/pi';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { LocationAutocompleteInput } from 'src/modules/shared/components/molecules/location-autocomplete-input';
import { LocationMapPicker } from 'src/modules/shared/components/molecules/location-map-picker';
import {
  useLocalities,
  useRegions,
} from 'src/modules/shared/hooks/api/location/location';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { LocationAutocompleteSuggestion } from 'src/modules/shared/types/location/geocoding.type';
import { reverseGeocodeGoogleMapsLocation } from 'src/modules/shared/utils/location/google-maps-geocoding.service';
import {
  buildPropertyGoogleMapsUrl,
  parsePropertyCoordinate,
} from '../../utils/property-location.util';
import {
  buildPropertyCoordinatePatch,
  propertyLocationFieldNames,
  buildPropertyLocationPatchFromSuggestion,
} from '../../utils/property-location-field.util';
import type { PropertyLocationEditorProps } from '../../types/property-location-fields.type';
import {
  buildLocationCountryOptions,
  getLocationCatalogCountryCode,
} from 'src/modules/shared/utils/location/country.util';

/**
 * Property-specific location editor built on top of the shared autocomplete
 * and map primitives.
 */
export const PropertyLocationEditor: React.FC<PropertyLocationEditorProps> = ({
  register,
  setValue,
  disabled,
  tenantCountryCode,
  values,
  onPendingChange,
}) => {
  const { t, language } = useI18n();
  const inputId = useId();
  const catalogCountry = getLocationCatalogCountryCode(values.countryCode);
  const { regions: regionData } = useRegions(
    catalogCountry,
    Boolean(catalogCountry)
  );
  const { localities: localityData } = useLocalities(
    catalogCountry,
    Boolean(catalogCountry)
  );
  const regions = catalogCountry
    ? regionData.filter((region) => region.countryCode === catalogCountry)
    : [];
  const localities = catalogCountry
    ? localityData.filter((locality) => locality.countryCode === catalogCountry)
    : [];
  const [searchCountryCode, setSearchCountryCode] = useState('');
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const latestValues = useRef(values);
  latestValues.current = values;
  const [reverseLoading, setReverseLoading] = useState(false);
  const [reverseError, setReverseError] = useState(false);
  const locationRevision = useRef(0);
  useEffect(() => {
    onPendingChange(reverseLoading || selectionLoading);
  }, [onPendingChange, reverseLoading, selectionLoading]);
  useEffect(
    () => () => {
      locationRevision.current += 1;
    },
    []
  );

  const countryOptions = useMemo(
    () => buildLocationCountryOptions(language, values.countryCode),
    [language, values.countryCode]
  );
  const searchCountryOptions = useMemo(
    () => [
      { content: t('catalog.properties.location.allCountries'), value: '' },
      ...Array.from(
        new Map(
          [
            ...countryOptions,
            ...buildLocationCountryOptions(language, searchCountryCode),
          ].map((option) => [option.value, option])
        ).values()
      ),
    ],
    [countryOptions, language, searchCountryCode, t]
  );
  const selectedRegion = regions.find(
    (region) => region.name === values.region
  );
  const localityOptions = localities.filter(
    (locality) => !selectedRegion || locality.regionCode === selectedRegion.code
  );

  const applySuggestion = useCallback(
    (suggestion: LocationAutocompleteSuggestion) => {
      const patch = buildPropertyLocationPatchFromSuggestion(
        suggestion,
        latestValues.current
      );

      locationRevision.current += 1;
      setSelectionResetKey((key) => key + 1);
      setReverseLoading(false);
      setReverseError(false);
      for (const fieldName of propertyLocationFieldNames) {
        setValue(fieldName, patch[fieldName] ?? '', {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [setValue]
  );

  const handleMapCoordinatesChange = useCallback(
    async (coordinates: { latitude: number; longitude: number }) => {
      const revision = ++locationRevision.current;
      setSelectionResetKey((key) => key + 1);
      setReverseError(false);
      const patch = buildPropertyCoordinatePatch(coordinates);
      setValue('latitude', patch.latitude, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue('longitude', patch.longitude, {
        shouldDirty: true,
        shouldTouch: true,
      });

      setReverseLoading(true);
      try {
        const suggestion = await reverseGeocodeGoogleMapsLocation(coordinates);
        if (revision !== locationRevision.current) return;
        if (suggestion) {
          applySuggestion({ ...suggestion, ...coordinates });
        } else {
          setReverseError(true);
        }
      } catch {
        if (revision === locationRevision.current) setReverseError(true);
      } finally {
        if (revision === locationRevision.current) setReverseLoading(false);
      }
    },
    [applySuggestion, setValue]
  );

  const handleCountryChange = (countryCode: string | undefined) => {
    setSelectionResetKey((key) => key + 1);
    if (countryCode === values.countryCode) return;
    locationRevision.current += 1;
    setReverseLoading(false);
    setReverseError(false);
    for (const fieldName of propertyLocationFieldNames) {
      setValue(
        fieldName,
        fieldName === 'countryCode' ? countryCode ?? '' : '',
        {
          shouldDirty: true,
          shouldTouch: true,
        }
      );
    }
  };

  const mapsUrl = buildPropertyGoogleMapsUrl(values);
  const latitude = parsePropertyCoordinate(values.latitude);
  const longitude = parsePropertyCoordinate(values.longitude);

  return (
    <div className="w-full space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_220px]">
        <div className="space-y-1">
          <Text size="small" weight="medium">
            {t('catalog.properties.location.autocomplete.title')}
          </Text>
          <LocationAutocompleteInput
            value={values.addressDisplay}
            selectionResetKey={selectionResetKey}
            countryCode={searchCountryCode || undefined}
            regionCode={tenantCountryCode}
            disabled={disabled}
            placeholder={t(
              'catalog.properties.location.autocomplete.placeholder'
            )}
            onSelectionStart={() => {
              locationRevision.current += 1;
              setReverseLoading(false);
              setReverseError(false);
            }}
            onSelectionPendingChange={setSelectionLoading}
            onSelect={applySuggestion}
          />
        </div>
        <div
          role="group"
          aria-label={t('catalog.properties.location.searchCountry')}
          className="space-y-1"
        >
          <Text size="small" weight="medium">
            {t('catalog.properties.location.searchCountry')}
          </Text>
          <SearchableSelect
            value={searchCountryCode}
            options={searchCountryOptions}
            disabled={disabled}
            onChange={(code) => setSearchCountryCode(code ?? '')}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="flex flex-col gap-1">
            <Label size="md" htmlFor={`${inputId}-street`}>
              {t('catalog.properties.form.streetName')}
            </Label>
            <TextInput
              id={`${inputId}-street`}
              disabled={disabled}
              {...register('streetName')}
              placeholder={t(
                'catalog.properties.location.streetNamePlaceholder'
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label size="md" htmlFor={`${inputId}-number`}>
                {t('catalog.properties.form.streetNumber')}
              </Label>
              <TextInput
                id={`${inputId}-number`}
                disabled={disabled}
                {...register('streetNumber')}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label size="md" htmlFor={`${inputId}-unit`}>
                {t('catalog.properties.form.unit')}
              </Label>
              <TextInput
                id={`${inputId}-unit`}
                disabled={disabled}
                {...register('unit')}
                placeholder={t('catalog.properties.location.unitPlaceholder')}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label size="md" htmlFor={`${inputId}-locality`}>
                {t('catalog.properties.location.locality')}
              </Label>
              <TextInput
                id={`${inputId}-locality`}
                {...register('locality')}
                list={`${inputId}-localities`}
                disabled={disabled}
                placeholder={t(
                  'catalog.properties.location.localityPlaceholder'
                )}
              />
              <datalist id={`${inputId}-localities`}>
                {localityOptions.map((locality) => (
                  <option key={locality.code} value={locality.name} />
                ))}
              </datalist>
            </div>
            <div className="flex flex-col gap-1">
              <Label size="md" htmlFor={`${inputId}-region`}>
                {t('catalog.properties.location.region')}
              </Label>
              <TextInput
                id={`${inputId}-region`}
                {...register('region')}
                list={`${inputId}-regions`}
                disabled={disabled}
                placeholder={t('catalog.properties.location.regionPlaceholder')}
              />
              <datalist id={`${inputId}-regions`}>
                {regions.map((region) => (
                  <option key={region.code} value={region.name} />
                ))}
              </datalist>
            </div>
            <div className="flex flex-col gap-1">
              <Label size="md" htmlFor={`${inputId}-neighborhood`}>
                {t('catalog.properties.form.neighborhood')}
              </Label>
              <TextInput
                id={`${inputId}-neighborhood`}
                disabled={disabled}
                {...register('neighborhood')}
              />
            </div>
            <div
              role="group"
              aria-label={t('catalog.properties.location.country')}
              className="flex flex-col gap-1"
            >
              <Text size="small">
                {t('catalog.properties.location.country')}
              </Text>
              <SearchableSelect
                value={values.countryCode ?? ''}
                options={countryOptions}
                disabled={disabled}
                placeholder={t(
                  'catalog.properties.location.countryPlaceholder'
                )}
                onChange={handleCountryChange}
              />
            </div>
          </div>
          <Text size="tiny" className="text-slate-500">
            {t('catalog.properties.location.countryHelp')}
          </Text>
          <div className="flex flex-col gap-1">
            <Label size="md" htmlFor={`${inputId}-public`}>
              {t('catalog.properties.form.addressDisplay')}
            </Label>
            <TextInput
              id={`${inputId}-public`}
              disabled={disabled}
              {...register('addressDisplay')}
              placeholder={t('catalog.properties.location.addressPlaceholder')}
            />
          </div>
          <input type="hidden" {...register('countryCode')} />
          <input type="hidden" {...register('latitude')} />
          <input type="hidden" {...register('longitude')} />
        </div>
        <div className="min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Text size="small" weight="medium">
              {t('catalog.properties.location.preview.title')}
            </Text>
            {mapsUrl ? (
              <Button
                type="button"
                size="xs"
                variant="neutral"
                appearance="ghost"
                onClick={() =>
                  window.open(mapsUrl, '_blank', 'noopener,noreferrer')
                }
                aria-label={t('catalog.properties.location.preview.open')}
              >
                <PiArrowSquareOut className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
          <LocationMapPicker
            latitude={latitude}
            longitude={longitude}
            countryCode={
              values.countryCode?.trim()
                ? values.countryCode
                : tenantCountryCode
            }
            disabled={disabled}
            onCoordinatesChange={(coordinates) => {
              void handleMapCoordinatesChange(coordinates);
            }}
          />
          <Text size="tiny" className="text-slate-500">
            {t(
              reverseLoading
                ? 'catalog.properties.location.map.reverseLoading'
                : 'catalog.properties.location.map.help'
            )}
          </Text>
          {reverseError ? (
            <div role="alert">
              <Text size="tiny" className="text-amber-700">
                {t('catalog.properties.location.map.reverseError')}
              </Text>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
